import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import type {
  BookedFlightItineraryWithDepartureTime,
  DisruptionProtectionDisruptionProtectionPolicyDetails,
} from "@b2bportal/air-booking-api";
import type {
  DisruptionRefundPlanRequest,
  PaymentOpaqueValue,
} from "@b2bportal/air-disruption-api";
import type { FiatPrice } from "@b2bportal/air-shopping-api";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  type AppDispatch,
  CoreDisruptionComponent,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  DisruptionExerciseFooter,
  DisruptionExerciseOptionsCard,
  DisruptionExercisePage,
  DisruptionExercisePageWidth,
  DisruptionExercisePageContent,
  DisruptionExercisePolicyDetails,
  DisruptionExerciseSummaryCard,
} from "@tenant/disruption";
import { getDisruptionExerciseProgress } from "../../../../features/exercise";
import {
  DisruptionExerciseProgress,
  resetDisruptionExerciseState,
  setDisruptionExerciseProgress,
} from "../../../../features/exercise/store/slice";
import { getDisruptionRefundPlan } from "../../../../features/exercise/thunks/disruptionRefundPlan";

import defaultStyles from "./DisruptionExerciseLandingPage.module.scss";

export interface DisruptionExerciseLandingPageProps {
  disruptionProductName: string;
  disruptionProductLogo: string;
  disruptedFlight: BookedFlightItineraryWithDepartureTime;
  contractId: string;
  setReimbursementTotalAmount: (amount: FiatPrice) => void;
  setRefundBreakdown: (breakdown: Array<PaymentOpaqueValue>) => void;
  setRefundFulfillmentToken: (token: string) => void;
  policyDetails: DisruptionProtectionDisruptionProtectionPolicyDetails;
  onContactSupport: () => void;
  termsLink: string;
  displayExternalLinkAsModal: boolean;
  delayHours: string;
  hoursString: string;
  rebookingLimit?: string;
  disruptionSelfServeRebook: boolean;
  cancelFlowAction?: () => void;
}

export const DisruptionExerciseLandingPage = ({
  disruptionProductName,
  disruptionProductLogo,
  disruptedFlight,
  contractId,
  setReimbursementTotalAmount,
  setRefundBreakdown,
  setRefundFulfillmentToken,
  policyDetails,
  onContactSupport,
  termsLink,
  displayExternalLinkAsModal,
  delayHours,
  hoursString,
  rebookingLimit,
  disruptionSelfServeRebook,
  cancelFlowAction,
}: DisruptionExerciseLandingPageProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();
  const COMPONENT_KEY = CoreDisruptionComponent.DisruptionExerciseLandingPage;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const dispatch = useDispatch<AppDispatch>();
  const disruptionExerciseProgress = useSelector(getDisruptionExerciseProgress);
  const [refundPlanError, setRefundPlanError] = useState<string | null>(null);
  const [policyDetailsExpanded, setPolicyDetailsExpanded] = useState(true);
  const [loading, setLoading] = useState<boolean>(false);
  const shouldEnableCta =
    disruptionExerciseProgress === DisruptionExerciseProgress.RebookSelected ||
    disruptionExerciseProgress === DisruptionExerciseProgress.RefundSelected;

  const fetchDisruptionRefundPlan = useCallback(() => {
    if (contractId) {
      const req: DisruptionRefundPlanRequest = {
        contractId,
        itineraryId: disruptedFlight.bookedItinerary.id,
      };

      return dispatch(getDisruptionRefundPlan(req))
        .then((payload) => {
          const response = unwrapResult(payload);
          setLoading(false);
          if (
            response?.DisruptionRefundPlanResponse ===
            "DisruptionRefundPlanSuccess"
          ) {
            setReimbursementTotalAmount(response.reimbursementAmount.fiat);
            if (response.refundBreakdown !== undefined) {
              setRefundBreakdown(response.refundBreakdown.refundLineItems);
            }
            if (response.token !== undefined) {
              setRefundFulfillmentToken(response.token.value);
            }

            dispatch(
              setDisruptionExerciseProgress(
                DisruptionExerciseProgress.RefundReview
              )
            );
          } else {
            handleRefundPlanError("DisruptionRefundPlanFailure");
          }
        })
        .catch((e) => {
          setLoading(false);
          handleRefundPlanError(e);
        });
    }
  }, [
    contractId,
    dispatch,
    disruptedFlight,
    setRefundBreakdown,
    setRefundFulfillmentToken,
    setReimbursementTotalAmount,
  ]);

  const handleRefundPlanError = (error: string) => {
    setRefundPlanError(error);
  };

  const handleCta = useCallback(() => {
    if (
      disruptionExerciseProgress === DisruptionExerciseProgress.RebookSelected
    ) {
      if (disruptionSelfServeRebook) {
        dispatch(
          setDisruptionExerciseProgress(DisruptionExerciseProgress.RebookReview)
        );
      } else {
        onContactSupport();
      }
    } else if (
      disruptionExerciseProgress === DisruptionExerciseProgress.RefundSelected
    ) {
      if (disruptedFlight.bookedItinerary.isHopperMor === true) {
        setLoading(true);
        fetchDisruptionRefundPlan();
      } else {
        onContactSupport();
      }
    }
  }, [
    disruptedFlight,
    disruptionExerciseProgress,
    fetchDisruptionRefundPlan,
    onContactSupport,
    dispatch,
    disruptionSelfServeRebook,
  ]);

  const onExpand = useCallback(() => {
    setPolicyDetailsExpanded(!policyDetailsExpanded);
  }, [setPolicyDetailsExpanded, policyDetailsExpanded]);

  const handleGoBack = () => {
    dispatch(resetDisruptionExerciseState());
  };

  const policyDetailsContent = (
    <DisruptionExercisePolicyDetails
      policyDetails={policyDetails}
      expanded={policyDetailsExpanded}
      onExpand={onExpand}
      termsLink={termsLink}
      displayExternalLinkAsModal={displayExternalLinkAsModal}
    ></DisruptionExercisePolicyDetails>
  );

  return (
    <DisruptionExercisePage
      className={cn("DisruptionExerciseLandingPage")}
      pageWidth={DisruptionExercisePageWidth.Large}
      title={disruptionProductName}
      disruptionProductLogo={disruptionProductLogo}
      onBack={handleGoBack}
      openErrorModal={refundPlanError !== null}
      onCloseErrorModal={() => {
        setRefundPlanError(null);
      }}
      onSupportClick={onContactSupport}
      submitButtonTitle={t(
        "core-disruption.disruptionUniversalExercise.continue"
      )}
      submitButtonAction={handleCta}
      submitButtonDisabled={!shouldEnableCta}
      isLoading={loading}
      termsLink={termsLink}
      onCancelFlow={cancelFlowAction}
      content={
        <DisruptionExercisePageContent
          leftContent={[
            <DisruptionExerciseSummaryCard
              title={t(
                "core-disruption.disruptionUniversalExercise.landingPage.title"
              )}
              description={t(
                "core-disruption.disruptionUniversalExercise.landingPage.subtitleWithRebookingLimit",
                {
                  rebookLimit: rebookingLimit,
                }
              )}
              key={"landing-page-header-content"}
            ></DisruptionExerciseSummaryCard>,
            <DisruptionExerciseOptionsCard
              onSupportClick={onContactSupport}
              key={"landing-page-options"}
            ></DisruptionExerciseOptionsCard>,
          ]}
          rightConent={[
            policyDetailsContent,
            isDesktopAndUp ? (
              <div></div>
            ) : (
              <DisruptionExerciseFooter
                onSupportClick={onContactSupport}
                termsLink={termsLink}
              ></DisruptionExerciseFooter>
            ),
          ]}
        />
      }
      delayHours={delayHours}
      numberOfHours={hoursString}
    ></DisruptionExercisePage>
  );
};

export default DisruptionExerciseLandingPage;
